import * as React from "react";

import {
  Autocomplete,
  AutocompleteState,
} from "Components/Shared/Inputs/Autocomplete";
import {
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
} from "Components/Shared/Icons";
import {
  EmployeeTypeCategoryID,
  EmployeeTypeID,
  getHumanResourceCompanyAutocomplete,
} from "Api/Api";
import { FieldError, UseFormMethods } from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";
import {
  StyledCheckbox,
  StyledCheckboxWrapper,
  StyledError,
  StyledTitle,
  StyledWrapper,
} from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditBasicInfo/EmployeeEditBasicInfoStyles";

import { AccessRightCodes } from "Components/Models/AccessRights";
import { Authorized } from "Routes/Authorized";
import { DatePicker } from "Components/Shared/Inputs/DatePicker";
import { Dropdown, DropdownOption } from "Components/Shared/Inputs/Dropdown";
import { EmployeeEditSupervisor } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditSupervisor";
import { EmployeeFormData } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditForm";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { Typography } from "@material-ui/core";
import { getEmployeeEditRight } from "Components/HumanResources/Employees/EmployeeDetail/EmployeeDetailPage";
import { nameof } from "Utils/ObjectUtils";
import { useDispatch, useSelector } from "react-redux";
import { getHumanResourcesEmployeeTypeListAsync } from "State/HumanResources/HumanResourcesReducer";
import { RootStateType } from "State/Store";

type Props = {
  form: UseFormMethods<EmployeeFormData>;
};

export const EmployeeEditBasicInfo: React.FunctionComponent<Props> = props => {
  const { register, watch, setValue, errors } = props.form;
  const { t } = useResource();
  const {
    phone,
    anniversaryDate,
    birthDate,
    isActive,
    employeeTypeID,
    employeeTypeCategoryID,
    companyID,
    companyName,
  } = watch();
  const dispatch = useDispatch();

  console.log("errors", errors);
  console.log("employeeTypeID", employeeTypeID);
  console.log("employeeTypeCategoryID", employeeTypeCategoryID);
  const [companySearch, setCompanySearch] = React.useState<AutocompleteState>({
    selectedItem: companyID
      ? { itemID: (companyID as unknown) as number, name: companyName ?? "" }
      : { itemID: 0, name: "" },
    options: [],
    isLoading: false,
  });

  const getStringInput = (name: keyof EmployeeFormData, label: string) => (
    <div className={name}>
      <TextInput
        label={label}
        name={name}
        inputRef={register}
        fieldError={errors[name] as FieldError}
      />
    </div>
  );

  React.useEffect(() => {
    register({ name: nameof<EmployeeFormData>("employeeTypeID") });
    register({ name: nameof<EmployeeFormData>("employeeTypeCategoryID") });
    register({ name: nameof<EmployeeFormData>("companyName") });
    register({ name: nameof<EmployeeFormData>("companyID") });
    register({ name: nameof<EmployeeFormData>("phone") });
    register({ name: nameof<EmployeeFormData>("anniversaryDate") });
    register({ name: nameof<EmployeeFormData>("birthDate") });
    register({ name: nameof<EmployeeFormData>("isActive") });
  }, [register]);

  const {
    employeeTypeList: employeeTypes,
    isEmployeeTypeListLoading: isLoadingEmployeeTypes,
  } = useSelector((e: RootStateType) => e.humanResources);

  React.useEffect(() => {
    dispatch(getHumanResourcesEmployeeTypeListAsync.request());
  }, [dispatch]);

  const handleClick = () => {
    setValue(nameof<EmployeeFormData>("isActive"), !isActive);
  };

  if (isLoadingEmployeeTypes || !employeeTypes) {
    return <></>;
  }

  const dropdownOptions: DropdownOption[] = employeeTypes.map(type => ({
    value: type.employeeTypeID,
    text: type.name,
  }));

  console.log("dropw", dropdownOptions);

  return (
    <div>
      <div className={"form-title"}>
        {t(Resources.HumanResources.EmployeeEdit.BasicInfo)}
      </div>
      <StyledWrapper>
        {getStringInput(
          "firstName",
          t(Resources.HumanResources.EmployeeEdit.FirstName.Label),
        )}
        {getStringInput(
          "lastName",
          t(Resources.HumanResources.EmployeeEdit.LastName.Label),
        )}
        {errors.firstName && (
          <StyledError className={"error-first-name"}>
            <Typography>{errors.firstName?.message}</Typography>
          </StyledError>
        )}
        {errors.lastName && (
          <StyledError className={"error-last-name"}>
            <Typography>{errors.lastName?.message}</Typography>
          </StyledError>
        )}

        {getStringInput(
          "email",
          t(Resources.HumanResources.EmployeeEdit.Email.Label),
        )}
        <div className={"phone"}>
          <TextInput
            value={phone?.split(" ").join("")}
            label={t(Resources.HumanResources.EmployeeEdit.Phone.Label)}
            name={nameof<EmployeeFormData>("phone")}
            fieldError={errors.phone}
            mask={"+000 000 000 000"}
            unmask={"typed"}
            type={"tel"}
            onMaskInputChange={value => {
              setValue(nameof<EmployeeFormData>("phone"), value, {
                shouldValidate: true,
              });
            }}
          />
        </div>

        {errors.email && (
          <StyledError className={"error-email"}>
            <Typography>{errors.email?.message}</Typography>
          </StyledError>
        )}
        {errors.phone && (
          <StyledError className={"error-phone"}>
            <Typography>{errors.phone?.message}</Typography>
          </StyledError>
        )}

        <Authorized
          accessRights={[
            AccessRightCodes.Core_HumanResources_ExternalSuppliers_Edit,
          ]}
        >
          <Dropdown
            fieldError={errors.employeeTypeID}
            className="contract-type"
            value={employeeTypeID}
            label="Typ kontaktu"
            onChange={event => {
              const newValue = event.target.value as EmployeeTypeID;
              const category = employeeTypes.filter(
                x => x.employeeTypeID === newValue,
              )[0].employeeTypeCategoryID;
              setValue("employeeTypeID", newValue);
              setValue("employeeTypeCategoryID", category);
            }}
            options={dropdownOptions}
          ></Dropdown>
        </Authorized>

        <Authorized
          accessRights={[getEmployeeEditRight(employeeTypeCategoryID)]}
        >
          <div className={"birth-date"}>
            <DatePicker
              value={birthDate || null}
              label={t(Resources.HumanResources.EmployeeEdit.BirthDate.Label)}
              onChange={val => {
                setValue(nameof<EmployeeFormData>("birthDate"), val ?? "");
              }}
            />
          </div>
        </Authorized>

        <EmployeeEditSupervisor
          label={t(Resources.HumanResources.EmployeeEdit.Supervisor.Label)}
          form={props.form}
        />

        {employeeTypeCategoryID === EmployeeTypeCategoryID.Internal && (
          <div className={"anniversary-date"}>
            <DatePicker
              value={anniversaryDate || null}
              label={t(
                Resources.HumanResources.EmployeeEdit.AnniversaryDate.Label,
              )}
              onChange={val => {
                setValue(
                  nameof<EmployeeFormData>("anniversaryDate"),
                  val ?? "",
                );
              }}
            />
          </div>
        )}
        <div className={"is-active"}>
          <StyledCheckboxWrapper>
            <StyledTitle className={"form-label"}>
              {t(Resources.HumanResources.EmployeeEdit.Status.Title)}
            </StyledTitle>
            <StyledCheckbox onClick={_ => handleClick()}>
              {isActive ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />}
              <Typography>
                {t(Resources.HumanResources.EmployeeEdit.Status.Label)}
              </Typography>
            </StyledCheckbox>
          </StyledCheckboxWrapper>
        </div>

        {employeeTypeCategoryID === EmployeeTypeCategoryID.External && (
          <>
            <div className="company-id">
              <Autocomplete
                value={companySearch.selectedItem}
                onSearch={text => {
                  setCompanySearch({ ...companySearch, isLoading: true });

                  getHumanResourceCompanyAutocomplete(text).then(result => {
                    setCompanySearch({
                      ...companySearch,
                      options: result.json,
                      isLoading: false,
                    });
                  });
                }}
                fieldError={errors.companyID}
                isLoading={companySearch.isLoading}
                options={companySearch.options}
                onChange={val => {
                  setValue("companyID", val?.itemID.toString(), {
                    shouldValidate: true,
                  });
                  setValue("companyName", val?.name);
                  setCompanySearch({
                    ...companySearch,
                    selectedItem: val,
                  });
                }}
                label="Společnost"
              />
            </div>
          </>
        )}
        {errors.companyID && (
          <StyledError className={"error-company-id"}>
            <Typography>{errors.companyID?.message}</Typography>
          </StyledError>
        )}
      </StyledWrapper>
    </div>
  );
};
